import { ROUTES } from '#/config/routes';
import {
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import MaterialStatusBadge from '#/modules/momMgmt/analysis/orders/components/MaterialStatusBadge';
import { MATERIAL_STATUS_FILTER_OPTIONS } from '#/modules/momMgmt/analysis/orders/helper/materialStatus';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import {
  readScheduledStartDateRangeFilter,
  resolveScheduledStartDateFilterChipDisplay,
} from '#/modules/momMgmt/workOrder/helper/scheduledStartDateListFilter';
import { WORK_ORDER_STATUS_FILTER_OPTIONS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function dash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function formatMtrLabel(mtr) {
  if (!mtr) {
    return '—';
  }
  const no = mtr.mtrNo || '';
  const name = mtr.name || '';
  if (no && name) {
    return `${no} ${name}`;
  }
  return no || name || '—';
}

function formatWarnings(warnings) {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return '—';
  }
  return warnings
    .map((item) => (typeof item === 'string' ? item : item?.message || item?.code))
    .filter(Boolean)
    .join('；') || '—';
}

/** 製造命令狀態表欄位（對齊 manufacturing-command-status `workOrders[]`）。 */
export function buildWorkOrderStatusColumns({
  filters = {},
  onFilterChange,
  onScheduledStartDateRangeChange,
  onMaterialStatusClick,
  processingFactoryOptions = [],
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.workOrderDetail(row.id),
      getEditPath: (row) => ROUTES.workOrderEdit(row.id),
    }),
    {
      id: 'workOrderNo',
      header: '工單號',
      accessorKey: 'workOrderNo',
      accessorFn: (row) => dash(row.workOrderNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'workOrderNo',
        label: '工單號',
        filterValue: filters.workOrderNo,
        onChange: (value) => onFilterChange?.('workOrderNo', value),
        placeholder: '搜尋工單號',
      }),
    },
    {
      id: 'mtr',
      header: '成品',
      accessorFn: (row) => formatMtrLabel(row.mtr),
      size: 220,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtr',
          label: '成品',
          filterValue: filters.mtr,
          onChange: (value) => onFilterChange?.('mtr', value),
          placeholder: '搜尋料號或名稱',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={formatMtrLabel(row.original.mtr)} />
      ),
    },
    {
      id: 'workOrderStatus',
      header: '工單狀態',
      accessorKey: 'workOrderStatus',
      size: 110,
      enableColumnFilter: true,
      cell: ({ row }) => (
        <WorkOrderStatusBadge status={row.original.workOrderStatus} size="sm" />
      ),
      meta: createFixedMultiFilterMeta({
        key: 'workOrderStatus',
        label: '工單狀態',
        options: WORK_ORDER_STATUS_FILTER_OPTIONS,
        filterValue: filters.workOrderStatus,
        onChange: (value) => onFilterChange?.('workOrderStatus', value),
      }),
    },
    {
      id: 'materialStatus',
      header: () => (
        <ColumnTipHeader
          label="物料狀態"
          tip="點擊狀態可查看物料需求配置"
        />
      ),
      accessorKey: 'materialStatus',
      size: 110,
      enableColumnFilter: true,
      cell: ({ row }) => (
        <MaterialStatusBadge
          status={row.original.materialStatus}
          size="sm"
          onClick={onMaterialStatusClick
            ? (event) => {
              event.stopPropagation();
              onMaterialStatusClick(row.original);
            }
            : undefined}
        />
      ),
      meta: {
        label: '物料狀態',
        ...createFixedMultiFilterMeta({
          key: 'materialStatus',
          label: '物料狀態',
          options: MATERIAL_STATUS_FILTER_OPTIONS,
          filterValue: filters.materialStatus,
          onChange: (value) => onFilterChange?.('materialStatus', value),
        }),
      },
    },
    {
      id: 'processingFactory',
      header: '加工廠',
      accessorFn: (row) => dash(row.processingFactory?.name),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createFixedMultiFilterMeta({
          key: 'processingFactoryId',
          label: '加工廠',
          options: processingFactoryOptions,
          filterValue: filters.processingFactoryId,
          onChange: (value) => onFilterChange?.('processingFactoryId', value),
        }),
      },
    },
    {
      id: 'workWarehouse',
      header: '工作倉',
      accessorFn: (row) => dash(row.workWarehouse?.name),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'workWarehouse',
          label: '工作倉',
          filterValue: filters.workWarehouse,
          onChange: (value) => onFilterChange?.('workWarehouse', value),
          placeholder: '搜尋工作倉',
        }),
      },
    },
    {
      id: 'scheduledStartDate',
      header: '排程日',
      accessorKey: 'scheduledStartDate',
      accessorFn: (row) => formatDateOrDash(row.scheduledStartDate),
      size: 120,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'scheduledStartDateFrom',
        label: '排程日',
        mode: 'range',
        filterValue: readScheduledStartDateRangeFilter(filters),
        onChange: onScheduledStartDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveScheduledStartDateFilterChipDisplay,
      }),
    },
    {
      id: 'originalDemandQty',
      header: '原始需求',
      accessorFn: (row) => formatDisplayQty(row.originalDemandQty),
      size: 100,
    },
    {
      id: 'finishedGoodsAllocatedQty',
      header: '成品抵扣',
      accessorFn: (row) => formatDisplayQty(row.finishedGoodsAllocatedQty),
      size: 100,
    },
    {
      id: 'plannedProductionQty',
      header: '計劃產量',
      accessorFn: (row) => formatDisplayQty(row.plannedProductionQty),
      size: 100,
    },
    {
      id: 'warnings',
      header: '警示',
      accessorFn: (row) => formatWarnings(row.warnings),
      size: 200,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={formatWarnings(row.original.warnings)} />
      ),
    },
  ];
}
